import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import DakkapelOptionsPanel from '../components/DakkapelOptionsPanel';
import DakkapelDrawingBoard from '../components/DakkapelDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import {
  glassTextures,
  glazingTypes,
  colors,
  woodJointOptions,
  PRICING,
} from '../data/mockData';
import {
  buildDefaultDakkapelConfig,
  normalizeDakkapelConfig,
  updateElementWidth,
  updateElementType,
  applyWidthDrivenLayout,
  clampDakkapelHeightMm,
} from '../data/dakkapelOptions';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';

const Dakkapel = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);

  const buildDefaultConfig = () =>
    withDefaultAdditionalItems({
      ...buildDefaultDakkapelConfig(),
    });

  const { config, setConfig, navLoadToken, lastNavLoadedConfig } = useConfiguratorLoadState(
    location,
    buildDefaultConfig,
    (loaded) => withDefaultAdditionalItems(normalizeDakkapelConfig(loaded || {}))
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);

  useEffect(() => {
    if (navLoadToken > 0 && lastNavLoadedConfig) {
      resetBaseline(lastNavLoadedConfig);
    }
  }, [navLoadToken, lastNavLoadedConfig, resetBaseline]);

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    resetBaseline(nextConfig);
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const handleConfigChange = (field, value) => {
    markDirty();
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfigPatch = (partial) => {
    markDirty();
    setConfig((prev) => ({
      ...prev,
      ...partial,
    }));
  };

  const handleElementWidthChange = useCallback((index, widthMm) => {
    markDirty();
    setConfig((prev) => ({
      ...prev,
      elements: updateElementWidth(prev.elements || [], index, widthMm, prev.width),
    }));
  }, [markDirty, setConfig]);

  const handleElementTypeChange = useCallback((index, type) => {
    markDirty();
    setConfig((prev) => ({
      ...prev,
      elements: updateElementType(prev.elements || [], index, type, prev.width),
    }));
  }, [markDirty, setConfig]);

  // Editing the width on the drawing re-splits the parts, same as the options panel.
  // Auto part-count follows the width bands unless Stap 2 locked a manual choice.
  const handleWidthChange = useCallback((widthMm) => {
    markDirty();
    setConfig((prev) => ({
      ...prev,
      ...applyWidthDrivenLayout(widthMm, prev.elements),
    }));
  }, [markDirty, setConfig]);

  const handleHeightChange = useCallback((heightMm) => {
    markDirty();
    setConfig((prev) => ({ ...prev, height: clampDakkapelHeightMm(heightMm) }));
  }, [markDirty, setConfig]);

  return (
    <ConfiguratorShell
      title={t('configurator.dakkapelConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      previewCentered
      optionsPanel={
        <div className="h-full overflow-y-auto overflow-x-hidden p-4 md:p-5 xl:p-6 bg-white border-r border-gray-200 touch-manipulation">
          <DakkapelOptionsPanel
            config={config}
            onConfigChange={handleConfigChange}
            onConfigPatch={handleConfigPatch}
            glazingTypes={glazingTypes}
            additionalItems={config.additionalItems || createDefaultAdditionalItems()}
            onAdditionalItemsChange={handleAdditionalItemsChange}
          />
        </div>
      }
      previewPanel={
        <div ref={drawingBoardRef} className="h-full w-full">
          <DakkapelDrawingBoard
            config={config}
            onElementWidthChange={handleElementWidthChange}
            onElementTypeChange={handleElementTypeChange}
            onWidthChange={handleWidthChange}
            onHeightChange={handleHeightChange}
          />
        </div>
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={[]}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={[]}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="dakkapel"
          onConfigChange={handleConfigChange}
          saveCurrentRef={saveCurrentRef}
          onSaved={(savedConfig, options) => {
            if (options?.resetToDefault) {
              handleReset();
              return;
            }
            resetBaseline(savedConfig || config);
          }}
        />
      }
    />
  );
};

export default Dakkapel;
